// src/pages/SavedIncidents.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Copy,
  CheckCircle2,
  AlertTriangle,
  X,
  Trash2,
} from "lucide-react";
import DetailSidebar from "../components/DetailSidebar";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";
import useUnlockTokens from "../hooks/useUnlockTokens";

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-sm shadow-[0_0_10px_rgba(56,189,248,0.05)]";

const ENDPOINT =
  "https://whiteintel.io/api/regular/app/v2/incidents/get_saved_incidents.php";

/* ───────────────── Small helpers ───────────────── */
function getJwtToken() {
  try {
    return (
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s.replace(" ", "T") + "Z"); // backend likely UTC
    return d.toLocaleString();
  } catch {
    return s;
  }
}

/* ───────────────── Dismissible Alert ───────────────── */
function AlertBanner({ kind = "success", children, onClose }) {
  const isSuccess = kind === "success";
  const border = isSuccess ? "border-emerald-500/30" : "border-red-500/30";
  const bg = isSuccess ? "bg-emerald-500/10" : "bg-red-500/10";
  const text = isSuccess ? "text-emerald-200" : "text-red-200";
  const Icon = isSuccess ? CheckCircle2 : AlertTriangle;
  return (
    <div role="alert" className={`relative rounded-lg ${border} ${bg} px-3 py-2 text-sm ${text}`}>
      <div className="flex items-start gap-2 pr-6">
        <Icon className="h-4 w-4 mt-[2px]" />
        <div className="leading-relaxed">{children}</div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute right-1.5 top-1.5 rounded-md p-1 text-inherit hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function ConfirmationModal({ isOpen, onClose, onConfirm, title, message }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop with Blur */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      {/* Modal Card */}
      <div className={`relative w-full max-w-md rounded-2xl ${frosted} p-6 animate-in zoom-in-95 duration-200`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-full bg-red-500/20 p-2">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
        </div>
        
        <p className="mb-6 text-sm text-neutral-400 leading-relaxed">
          {message}
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-4 py-2 text-sm font-medium text-neutral-300 hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors shadow-lg shadow-red-900/20"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Small UI bits ───────────────── */
function TypeBadge({ type }) {
  const t = (type || "").toLowerCase();
  const map = {
    stealer: "bg-red-500/20 text-red-200 border-red-500/30",
    combolist: "bg-amber-500/20 text-amber-200 border-amber-500/30",
    database: "bg-indigo-500/20 text-indigo-200 border-indigo-500/30"
  };
  const cls = map[t] || "bg-neutral-700/30 text-neutral-200 border-neutral-600/40";
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs ${cls}`}>
      {t || "—"}
    </span>
  );
}

function CopyInline({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || "");
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {}
      }}
      className="rounded-md p-1 text-sky-300 hover:text-white hover:bg-white/10"
      title={copied ? "Copied" : "Copy"}
    >
      {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

/* ───────────────── Page ───────────────── */
export default function SavedIncidents() {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { refresh } = useUnlockTokens({ refreshMs: 0 }); // manual refresh

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true); // no totals -> assume more until we get 0 back
  const [apiError, setApiError] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailInitialParams, setDetailInitialParams] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, row: null });
  // in case backend later adds perPage; we can change this and also send it
  const PER_PAGE_HINT = 25;

  const detailTypeMap = {
    consumerRecords: "consumerRecords",
    corporateRecords: "corporateRecords",
    apkPackage: "apkPackage",
    attackSurface: "attackSurface",
    emailAddress: "emailAddress",
    ipAddress: "ipAddress",
    computerName: "computerName",
  };

  const triggerDelete = (r) => {
    setConfirmDelete({ isOpen: true, row: r });
  };

  const executeDelete = async () => {
    const r = confirmDelete.row;
    if (!r) return;

    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ 
          id: r.inherit_id, 
          type: r.breach_type, 
          action: "delete" 
        }),
      });

      const data = await res.json();
      if (!res.ok || data?.success !== true) throw new Error(data?.error || "Delete failed.");

      setRows((prev) => prev.filter((item) => item.id !== r.id));
    } catch (e) {
      setApiError(e.message);
    }
  };

  const handleView = (r) => {
    const body = {
      id: Number(r?.inherit_id),
      type: String(r?.breach_type || "stealer"),
      detail_type: detailTypeMap[r.classification],
      query: r?.application || "",
    };
    setDetailRow(r);
    setDetailInitialParams(body);
    setDetailOpen(true);
  };

  const mergeAppend = useCallback((existing, incoming) => {
    const seen = new Set(existing.map((x) => x.id));
    const deduped = [...existing];
    for (const it of incoming) {
      if (!seen.has(it.id)) {
        seen.add(it.id);
        deduped.push(it);
      }
    }
    return deduped;
  }, []);

  const load = useCallback(
    async (targetPage, append) => {
      try {
        if (append) setLoadingMore(true);
        else setLoadingFirst(true);
        setApiError(null);

        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          // If the API ever supports perPage, uncomment and send it:
          // body: JSON.stringify({ page: targetPage, perPage: PER_PAGE_HINT }),
          body: JSON.stringify({ page: targetPage }),
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        if (!res.ok || data?.success !== true) {
          throw new Error(data?.error || "Failed to fetch saved incidents.");
        }

        const list = Array.isArray(data?.data) ? data.data : [];

        setRows((prev) => (append ? mergeAppend(prev, list) : list));
        setPage(targetPage);
        // Stop when we receive 0 items
        setHasMore(list.length > 0);
      } catch (e) {
        console.error(e);
        setApiError(e.message || "Failed to load saved incidents.");
        if (!append) setRows([]);
        // On error during append, don't flip hasMore to false — let user retry
      } finally {
        setLoadingFirst(false);
        setLoadingMore(false);
      }
    },
    [mergeAppend]
  );

  useEffect(() => {
    load(1, false);
  }, [load]);

  const viewMore = () => {
    if (!loadingMore && hasMore) load(page + 1, true);
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-transparent px-6 py-4 transition-all duration-300"
        style={{ marginLeft: collapsed ? "72px" : "240px" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-[24px] font-bold leading-tight">Saved Incidents</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        {/* Content */}
        <main className="relative flex-1 px-4 md:px-6">
    

          {/* Alerts */}
          <div className="mt-4 space-y-3">
            {apiError && (
              <AlertBanner kind="error" onClose={() => setApiError(null)}>
                {apiError}
              </AlertBanner>
            )}
          </div>

          {/* Table Card */}
          <div className={`mt-6 rounded-2xl ${frosted} p-4`}>
            <div className="flex items-center justify-between border-b border-sky-900/30 pb-3">
              <div className="text-sm text-neutral-400">
                {loadingFirst ? "Loading…" : `${rows.length} loaded`}
              </div>
            </div>

            {/* Table */}
            <div className="mt-3 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0a1224]/90 backdrop-blur-sm">
                  <tr className="text-neutral-400 border-b border-sky-900/30">
                    <th className="px-2 py-3 text-left font-semibold">ID</th>
                    <th className="px-2 py-3 text-left font-semibold">Account</th>
                    <th className="px-2 py-3 text-left font-semibold">Application</th>
                    <th className="px-2 py-3 text-left font-semibold">Type</th>
                    <th className="px-2 py-3 text-left font-semibold">Saved At</th>
                    <th className="px-2 py-3 text-left font-semibold">Class.</th>
                    <th className="px-2 py-3 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800/80">
                  {loadingFirst ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={`sk-${i}`} className="animate-pulse">
                        <td className="px-2 py-3 text-neutral-600">••••</td>
                        <td className="px-2 py-3 text-neutral-600">Loading…</td>
                        <td className="px-2 py-3 text-neutral-600">—</td>
                        <td className="px-2 py-3">
                          <div className="h-5 w-16 bg-neutral-700/40 rounded" />
                        </td>
                        <td className="px-2 py-3">
                          <div className="h-5 w-20 bg-neutral-700/40 rounded" />
                        </td>
                        <td className="px-2 py-3 text-neutral-600">—</td>
                        <td className="px-2 py-3 text-neutral-600">—</td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-neutral-400">
                        No saved incidents yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.id} className="hover:bg-white/5">
                        <td className="px-2 py-3">{r.inherit_id}</td>
                        <td className="px-2 py-3 break-all">
                          <div className="flex items-center gap-2">
                            <span>{r.account || "—"}</span>
                            {r.account && <CopyInline text={r.account} />}
                          </div>
                        </td>
                        <td className="px-2 py-3 break-all">
                          <div className="flex items-center gap-2">
                            <span>{r.application || "—"}</span>
                            {r.application && <CopyInline text={r.application} />}
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <TypeBadge type={r.breach_type} />
                        </td>
                        <td className="px-2 py-3">{formatDate(r.saved_at)}</td>
                        <td className="px-2 py-3">
                          <span className="text-neutral-300">{r.classification || "—"}</span>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleView(r)}
                              className="rounded-lg border border-sky-600/40 bg-transparent px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-500/10"
                            >
                              View
                            </button>
<button
        onClick={() => triggerDelete(r)} // Call the trigger instead of execute
        className="rounded-lg border border-red-500/40 bg-transparent p-2 text-red-400 hover:bg-red-500/10 transition-colors"
      >
        <Trash2 className="h-4 w-4" />
      </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* View More */}
            <div className="mt-4 flex items-center justify-center">
              <button
                onClick={viewMore}
                disabled={loadingMore || !hasMore}
                className="rounded-md border border-neutral-700 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : hasMore ? "View more" : "No more results"}
              </button>
            </div>
          </div>
        </main>
      </div>

      <DetailSidebar
        open={detailOpen}
        row={detailRow}
        initialParams={detailInitialParams}
        jwtToken={getJwtToken()}
        onClose={() => setDetailOpen(false)}
        onUnlocked={refresh}
      />
      <ConfirmationModal
        isOpen={confirmDelete.isOpen}
        title="Confirm Deletion"
        message="This action is permanent. Are you sure you want to remove this incident from your saved list?"
        onClose={() => setConfirmDelete({ isOpen: false, row: null })}
        onConfirm={executeDelete}
      />
    </div>
  );
}
